/**
 * @file Analytics helpers for the upload manager (kind → feature, session outcome labels).
 */

import type {
  Features,
  UploadEnqueueSource,
  UploadFilesRejectedReason,
  UploadSessionCompletedOutcome,
} from '@/lib/analytics/events';

import type { UploadSessionOutcome } from './upload-session-outcome';

/** Kind → analytics feature. Keep in sync with registered upload adapters. */
const UPLOAD_KIND_FEATURES: Record<string, Features> = {
  'transition-document': 'property_transitions',
};

/**
 * Maps a registered upload kind to the analytics `feature` base payload value.
 */
export const getUploadAnalyticsFeature = (
  uploadKind: string
): Features | undefined => UPLOAD_KIND_FEATURES[uploadKind];

/**
 * Derives a low-cardinality session outcome label from {@link summarizeUploadSession}.
 */
export const getUploadSessionCompletedOutcome = (
  outcome: UploadSessionOutcome
): UploadSessionCompletedOutcome => {
  if (outcome.isFullSuccess) {
    return 'full_success';
  }

  if (outcome.completeCount > 0) {
    return 'partial_success';
  }

  if (outcome.errorCount > 0) {
    return 'all_failed';
  }

  return 'all_cancelled';
};

/**
 * Infers enqueue/reject source from a dropzone drop event.
 * Drag events use `drop`; file picker uses `change` (or similar).
 */
export const getUploadEnqueueSourceFromDropEvent = (
  event?: unknown
): UploadEnqueueSource => {
  if (
    event != null &&
    typeof event === 'object' &&
    'type' in event &&
    (event as { type?: unknown }).type === 'drop'
  ) {
    return 'drag_drop';
  }

  return 'file_picker';
};

/**
 * Maps react-dropzone rejection error codes to a low-cardinality reason.
 */
export const getUploadFilesRejectedReason = (
  hasInvalidType: boolean
): UploadFilesRejectedReason => (hasInvalidType ? 'invalid_type' : 'other');
